'use client';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { useSafeLanguage } from '@/contexts/LanguageContext';
import { breakingApi } from '@/services/api';
import type {
  BreakingEventsListParams,
  BreakingNewsCategoriesResponse,
  BreakingNewsCategory,
  BreakingNewsEventsListResponse,
  BreakingNewsMarket,
} from '@/types/breaking.types';

export const BREAKING_QUERY_KEYS = {
  all: ['breaking'] as const,
  categories: (locale: string) => [...BREAKING_QUERY_KEYS.all, 'categories', locale] as const,
  eventsList: (params: BreakingEventsListParams) =>
    [...BREAKING_QUERY_KEYS.all, 'eventsList', params] as const,
};

export const useBreakingCategories = () => {
  const { language } = useSafeLanguage();
  return useQuery<BreakingNewsCategoriesResponse, Error, BreakingNewsCategory[]>({
    queryKey: BREAKING_QUERY_KEYS.categories(language.code),
    queryFn: () => breakingApi.getBreakingCategories(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    select: (response) => response.data,
  });
};

export const useBreakingEventsList = (params: BreakingEventsListParams) => {
  return useQuery<BreakingNewsEventsListResponse, Error, BreakingNewsMarket[]>({
    queryKey: BREAKING_QUERY_KEYS.eventsList(params),
    queryFn: () => breakingApi.getBreakingEventsList(params),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    select: (response) => response.data,
  });
};

// Fetch breaking news events list, paginated for "Load More" (mirrors
// useMarketsInfinite: next page's offset stops once a page comes back
// shorter than the requested limit).
export const useBreakingEventsListInfinite = (params: BreakingEventsListParams) => {
  const initialOffset = params.offset || 0;
  return useInfiniteQuery<BreakingNewsEventsListResponse>({
    queryKey: [...BREAKING_QUERY_KEYS.eventsList(params), 'infinite'],
    queryFn: ({ pageParam = initialOffset }) =>
      breakingApi.getBreakingEventsList({ ...params, offset: pageParam as number }),
    initialPageParam: initialOffset,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.data.length < params.limit) return undefined;
      return initialOffset + allPages.length * params.limit;
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Same request/cache entry as useBreakingEventsList (identical queryKey, so
 * React Query dedupes the fetch), just projected to the response's `meta`
 * (generated_at, count) instead of the market list.
 */
export const useBreakingEventsMeta = (params: BreakingEventsListParams) => {
  return useQuery<BreakingNewsEventsListResponse, Error, BreakingNewsEventsListResponse['meta']>({
    queryKey: BREAKING_QUERY_KEYS.eventsList(params),
    queryFn: () => breakingApi.getBreakingEventsList(params),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    select: (response) => response.meta,
  });
};
